import { useCallback, useEffect, useMemo, useState } from 'react'
import ContentLoader from 'react-content-loader'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { ContentLayout } from 'components/Layouts'
import { getUserData, setUser, setErrors, getFormattedAddress } from 'redux/reducers/users'
import { getModalType, setModal, closeModal } from 'redux/reducers/modal'
import { AccountSettingsUpdateNameAndAddress } from 'components/MyDrawer/drawers'
import { Card, ICardProps } from 'components/Card/Card'
import { Divider } from 'components/Divider/Divider'
import { DrawerCloseReason } from 'components/MyDrawer'
import { EmailAndDocumentSettings } from '../components/EmailAndDocumentSettings'
import { EmailAddressModal } from '../components/EmailAddressModal/EmailAddressModal'
import { MedicareYouHandbookModal } from '../components/MedicareYouHandbookModal/MedicareYouHandbookModal'
import { EditMsnSettings } from '../components/EditMsnSettings'
import { medicareApiClient } from 'clients/medicareApiClient'
import { useAsyncInitialization } from 'hooks/useAsyncInitialization'
import { PasswordModal } from '../components/PasswordModal/PasswordModal'
import { EditUsernameModal } from '../components/UsernameModal/EditUsernameModal'
import { SecretQuestionModal } from '../components/SecretQuestionModal'
import { AccountSettingsModalTypes } from 'models/Modal'
import { DataSharing } from '../components/DataSharing/DataSharing'
import { HomeHeaderButtons } from '../components/HomeHeaderButtons'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { MfaSettings } from '../components/MFASettings/MfaSettings'
import { useQueryParams } from 'hooks/useQueryParams'
import { Component } from '@cms-max/medicare-api-sdk'

// TODO - rename this to AccountSettings
export interface AccountSettingsProps {}

const defaultCardProps: Partial<ICardProps> = {
  headingLevel: '3',
  className: 'my-u-mb--8'
}

export const AccountSettings: React.FC<AccountSettingsProps> = () => {
  const modalTypeOpen = useSelector(getModalType)
  const userData = useSelector(getUserData)
  const formattedMailingAddress = useSelector(getFormattedAddress)

  const flags = useFlags()

  const [documentSettingsError, setDocumentSettingsError] = useState(false)
  const [elmoUnavailable, setElmoUnavailable] = useState(false)

  const queryParams = useQueryParams()
  const updatedMfa = useMemo(() => Boolean(queryParams.get('updated_mfa_method')), [queryParams])

  const dispatch = useDispatch()

  const openNameAddressDrawer = () => {
    dispatch(setModal({ modalType: 'acct-name-address' }))
  }
  const closeNameAddressDrawer = (reason: DrawerCloseReason) => {
    dispatch(setModal({ modalType: undefined }))
  }

  const { t } = useTranslation('accountSettings')

  const openModal = (modalType: AccountSettingsModalTypes) => dispatch(setModal({ modalType }))

  const handleClose = () => dispatch(closeModal())

  const init = useCallback(async () => {
    const fullBene = await medicareApiClient.asyncGetBeneficiaryFullDataWithAccountSettings()
    if (fullBene) {
      dispatch(setUser(fullBene))

      if (fullBene?.user._errors && Object.keys(fullBene?.user._errors).length) {
        dispatch(setErrors(fullBene.user._errors))
      }
    } else {
      throw new Error('Full bene response is undefined')
    }
  }, [dispatch])
  const loadingState = useAsyncInitialization(init)

  // Convert API errors to state variables
  useEffect(() => {
    setElmoUnavailable(false)
    setDocumentSettingsError(false)
    if (userData?.errors?.eHandbookPreferences === 'ELMO offline') {
      setElmoUnavailable(true)
    } else if (userData?.errors?.eHandbookPreferences === 'Not available') {
      setDocumentSettingsError(true)
    } else if (userData?.errors?.eHandbookPreferences) {
      setDocumentSettingsError(true)
    }
  }, [userData.errors])

  useEffect(() => {
    if (updatedMfa) {
      const mfaSection = document.getElementById('mfa-card')
      mfaSection?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      })
    }
  }, [updatedMfa])

  const MyLoader = () => (
    <ContentLoader
      width={'100%'}
      height={750}
      data-testid="content-loader"
      title={t('skeletonLoading')}
      uniqueKey="my-account-settings-loader-1"
    >
      {/* Only SVG shapes */}
      <rect x="0" y="0" rx="5" ry="5" width="20%" height="32" />
      <rect x="0" y="40" rx="5" ry="5" width="25%" height="20" />
      <rect x="0" y="68" rx="5" ry="5" width="25%" height="20" />
      <rect x="35%" y="0" rx="5" ry="5" width="65%" height="200" />

      <rect x="0" y="250" rx="5" ry="5" width="20%" height="32" />
      <rect x="0" y="290" rx="5" ry="5" width="25%" height="20" />
      <rect x="0" y="318" rx="5" ry="5" width="25%" height="20" />
      <rect x="35%" y="250" rx="5" ry="5" width="65%" height="200" />

      <rect x="0" y="500" rx="5" ry="5" width="20%" height="32" />
      <rect x="0" y="540" rx="5" ry="5" width="25%" height="20" />
      <rect x="0" y="568" rx="5" ry="5" width="25%" height="20" />
      <rect x="35%" y="500" rx="5" ry="5" width="65%" height="200" />
    </ContentLoader>
  )

  return (
    <ContentLayout heading={t('pageTitle')} width='wide'>
      {modalTypeOpen === 'acct-name-address' && (
        <AccountSettingsUpdateNameAndAddress isRRB={userData.user?.railroadRetirementBoardBeneficiary} onClose={closeNameAddressDrawer} />
      )}
      {modalTypeOpen === 'acct-add-edit-email' && (
        <EmailAddressModal onClose={handleClose} />
      )}
      {modalTypeOpen === 'acct-msn-settings' && (
        <EditMsnSettings onClose={handleClose} />
      )}
      {modalTypeOpen === 'acct-handbook-settings' && (
        <MedicareYouHandbookModal onClose={handleClose} />
      )}
      {modalTypeOpen === 'acct-username' && (
        <EditUsernameModal onClose={handleClose} />
      )}
      {modalTypeOpen === 'acct-password' && (
        <PasswordModal onClose={handleClose} />
      )}
      {modalTypeOpen === 'acct-secret-question' && (
        <SecretQuestionModal onClose={handleClose} />
      )}
      <div className='ds-l-container my-u-pb--40'>
        <HomeHeaderButtons
          openNameAddressDrawer={openNameAddressDrawer}
          name={{
            first: userData.user?.firstName,
            middle: userData.user?.middleName,
            last: userData.user?.lastName
          }}
          formattedMailingAddress={formattedMailingAddress}
        />
        <Divider className='my-u-my--20' />
        {loadingState !== 'finished' && (
          <MyLoader />
        )}
        {loadingState === 'finished' && (
          <>
            <EmailAndDocumentSettings
              elmoUnavailable={elmoUnavailable}
              eHandbookPreferences={userData.user?.eHandbookPreferences}
              eMsnPreferences={userData.user?.eMsnPreferences}
              openModal={openModal}
              serverError={documentSettingsError}
              defaultCardProps={defaultCardProps}
              userEmail={userData.user?.email}
            />
            <Divider className='my-u-my--20' />
            <div className='ds-l-row'>
              <div className='ds-l-md-col--12 ds-l-lg-col--4'>
                <h2 className='ds-text-heading--xl ds-u-margin-bottom--2'>{t('paragraph.loginTitle')}</h2>
                <p>{t('paragraph.loginInfo')}</p>
              </div>
              <div className='ds-l-md-col--12 ds-l-lg-col--8'>
                <Card
                  key="cardUsername"
                  dataTestId="cardUsername"
                  title={t('cards.username')}
                  displayValue={userData.user?.username}
                  animationSubject={userData.user?.username}
                  buttonSiteActivityProps={{
                    core: {
                      component: Component.AccountSettings,
                      elementId: 'accountsettings_usernameedit_click',
                      coreClickData: {
                        linkText: 'Edit'
                      }
                    }
                  }}
                  buttonCallbackOrHref={() => openModal('acct-username')}
                  editButtonAriaLabel={t('btns.editUsernameAriaLabel')}
                  buttonText={t('btns.edit')}
                  {...defaultCardProps}
                />
                <Card
                  key="cardPassword"
                  dataTestId="cardPassword"
                  title={t('cards.password')}
                  displayValue="••••••••••"
                  animationSubject={userData.itemsLastUpdated?.password}
                  buttonSiteActivityProps={{
                    core: {
                      component: Component.AccountSettings,
                      elementId: 'accountsettings_passwordedit_click',
                      coreClickData: {
                        linkText: 'Edit'
                      }
                    }
                  }}
                  buttonCallbackOrHref={() => openModal('acct-password')}
                  editButtonAriaLabel={t('btns.editPasswordAriaLabel')}
                  buttonText={t('btns.edit')}
                  {...defaultCardProps}
                />
                <Card
                  key="cardSecretQuestion"
                  dataTestId="cardSecretQuestion"
                  title={t('cards.secretQuestion')}
                  displayValue={userData.user?.challenges?.[0]?.question}
                  animationSubject={userData.user?.challenges?.[0]?.question}
                  buttonSiteActivityProps={{
                    core: {
                      component: Component.AccountSettings,
                      elementId: 'accountsettings_secretedit_click',
                      coreClickData: {
                        linkText: 'Edit'
                      }
                    }
                  }}
                  buttonCallbackOrHref={() => openModal('acct-secret-question')}
                  editButtonAriaLabel={t('btns.editSecretQuestionAriaLabel')}
                  buttonText={t('btns.edit')}
                  {...defaultCardProps}
                />
              </div>
            </div>
          </>
        )}
        {flags.multiFactorAuthenticationDisplay && (
          <>
            <Divider className='my-u-my--20' />
            <MfaSettings />
          </>
        )}
        <Divider className='my-u-my--20' />
        <DataSharing />
      </div>
    </ContentLayout>
  )
}
